import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '@core/services/auth.service';
import { EMatchingType } from '@enums/campaign-analytics.enum';
import { IAnalyticsNode, ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { HoverCardComponent } from '@shared/components/hover-card/hover-card.component';
import { HoverCardContentDirective } from '@shared/components/hover-card/hover-card.directive';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatrixGridComponent } from '@shared/components/matrix-grid/matrix-grid.component';
import {
  MatrixCellDefDirective,
  MatrixExpandedDefDirective,
  MatrixFooterDefDirective,
  MatrixHeaderDefDirective,
} from '@shared/components/matrix-grid/matrix-grid.directive';
import { IMatrixGridSort } from '@shared/components/matrix-grid/matrix-grid.interface';
import { SegmentedControlComponent } from '@shared/components/segmented-control/segmented-control.component';
import { ISegmentedControlOption } from '@shared/components/segmented-control/segmented-control.interface';
import { SlotBarComponent } from '@shared/components/slot-bar/slot-bar.component';
import { ISlotBarSlot } from '@shared/components/slot-bar/slot-bar.interface';
import { getBarToneBackground, getSlotBarLayout } from '@shared/components/slot-bar/slot-bar.util';
import { StackedBarComponent } from '@shared/components/stacked-bar/stacked-bar.component';
import { StatusLegendComponent } from '@shared/components/status-legend/status-legend.component';
import { IStatusLegendItem } from '@shared/components/status-legend/status-legend.interface';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { DATE_FORMAT, DateUtilitiesService } from '@utilities/date-utilities.service';
import { DeliverableUtilitiesService } from '@utilities/deliverable-utilities.service';
import { NumberUtilitiesService } from '@utilities/number-utilities.service';
import { isAfter, isBefore, startOfDay } from 'date-fns';
import { NgxFloatUiModule, NgxFloatUiPlacements, NgxFloatUiTriggers } from 'ngx-float-ui';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { map, startWith } from 'rxjs';
import {
  IDeepLinkContentsEvent,
  IDeepLinkCreatorInfo,
  IDeepLinkDeliverablesEvent,
} from '../campaign-details-deep-link/campaign-details-deep-link.interface';
import { CampaignDetailsDeliverablesService } from '../campaign-details-deliverables/campaign-details-deliverables.service';
import { ECampaignDetailsTab, ICampaignAnalyticsFilter } from '../campaign-details.interface';
import { ICampaignExportConfig } from '../campaign-export-config/campaign-export-config.interface';
import { CampaignDetailsGoalsCollapseStateService } from './campaign-details-goals-collapse-state.service';
import { buildGoalsCsv, downloadCsvFile, getGoalsCsvFileName } from './campaign-details-goals-export.util';
import {
  EGoalsLens,
  EGoalsSortKey,
  IGoalsAnalyticsRequest,
  IGoalsCell,
  IGoalsCellView,
  IGoalsColumnHeaderView,
  IGoalsContentsLink,
  IGoalsDeepLink,
  IGoalsDeliverableRow,
  IGoalsDeliverablesLink,
  IGoalsGridColumn,
  IGoalsGridRow,
  IGoalsKpiRow,
  IGoalsSlot,
  IGoalsSort,
  IGoalsSubRowView,
} from './campaign-details-goals.interface';
import {
  areAllGoalsRowsExpanded,
  buildGoalsDeliverablesLinkKeys,
  buildGoalsMatrix,
  getGoalsCellLinks,
  getGoalsCellNotes,
  getGoalsColumnCapacity,
  getGoalsCountWidth,
  getGoalsFooterSummary,
  getGoalsKpiHeaderText,
  getGoalsLinkGroup,
  getGoalsPlanTotal,
  getGoalsShownSort,
  getGoalsSlotLabelKey,
  GOALS_COUNTED_MATCHING_TYPES,
  GOALS_RELATED_MATCHING_TYPES,
  hasGoalsDeliverablesLinkRows,
  hasGoalsKpiBarSlotsWithoutGoals,
  sortGoalsRows,
} from './campaign-details-goals.util';

const CREATOR_COLUMN_WIDTH = '220px';
const PLAN_COLUMN_WIDTH = '140px';
/** At least the header's room; wider when a cell needs it (long bar, count, "+n extra" chip, longer translations). */
const GROUP_COLUMN_WIDTH = 'minmax(260px, max-content)';
/** Skeleton group columns until the campaign (its deliverable groups) has loaded. */
const PLACEHOLDER_COLUMN_COUNT = 2;

@Component({
  imports: [
    HoverCardComponent,
    HoverCardContentDirective,
    IconBadgeComponent,
    IconComponent,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatrixCellDefDirective,
    MatrixExpandedDefDirective,
    MatrixFooterDefDirective,
    MatrixGridComponent,
    MatrixHeaderDefDirective,
    NgTemplateOutlet,
    NgxFloatUiModule,
    NgxSkeletonLoaderComponent,
    SegmentedControlComponent,
    SlotBarComponent,
    StackedBarComponent,
    StatusLegendComponent,
    TranslateModule,
  ],
  providers: [CampaignDetailsDeliverablesService],
  selector: 'app-campaign-details-goals',
  templateUrl: './campaign-details-goals.component.html',
})
export class CampaignDetailsGoalsComponent {
  private readonly translate = inject(TranslateService);
  private readonly analyticsUtilities = inject(CampaignAnalyticsUtilitiesService);
  private readonly deliverableUtilities = inject(DeliverableUtilitiesService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly numberUtilities = inject(NumberUtilitiesService);
  private readonly collapseState = inject(CampaignDetailsGoalsCollapseStateService);
  private readonly deliverablesService = inject(CampaignDetailsDeliverablesService);

  public readonly analyticsNodes = input.required<IAnalyticsNode[] | null>();
  public readonly campaign = input.required<TCampaignApi | null>();
  public readonly campaignAnalytics = input.required<ICampaignAnalytics | null>();
  public readonly campaignAnalyticsFilter = input.required<ICampaignAnalyticsFilter>();
  public readonly campaignExportConfig = input.required<ICampaignExportConfig>();
  public readonly exportMode = input<boolean>(false);
  public readonly loading = input<boolean>(true);
  /** The first data load failed, so there is nothing to show (a failed refresh keeps the last data). */
  public readonly loadError = input<boolean>(false);
  public readonly deepLink = input<IGoalsDeepLink | null>(null);
  /** False when the Contents tab is hidden (Shared Report tab selection): counts and notes stay plain text. */
  public readonly canOpenContents = input<boolean>(true);
  /** False when the Deliverables tab is hidden: group, issue and deliverable texts stay plain. */
  public readonly canOpenDeliverables = input<boolean>(true);

  public readonly analyticsRequested = output<IGoalsAnalyticsRequest>();
  public readonly contentsRequested = output<IDeepLinkContentsEvent>();
  public readonly deliverablesRequested = output<IDeepLinkDeliverablesEvent>();

  public readonly EGoalsLens = EGoalsLens;
  public readonly EGoalsSortKey = EGoalsSortKey;
  public readonly EMatchingType = EMatchingType;
  public readonly floatUiPlacementBottomStart = NgxFloatUiPlacements.BOTTOMSTART;
  public readonly floatUiTriggerClick = NgxFloatUiTriggers.click;
  public readonly getBarToneBackground = getBarToneBackground;
  /** Ring of a plan / hidden-column dot that is not complete yet. */
  public readonly OPEN_DOT_BORDER = '1.5px solid var(--shikenso-gray-400)';

  private readonly grid = viewChild<MatrixGridComponent<IGoalsGridRow, IGoalsGridColumn>>(MatrixGridComponent);

  public readonly lens = signal<EGoalsLens>(EGoalsLens.deliverables);
  public readonly sort = signal<IGoalsSort>({ direction: 'asc', key: EGoalsSortKey.planComplete });
  public readonly collapsedColumnIds = signal<string[]>([]);
  public readonly expandedRowIds = signal<string[]>([]);
  public readonly legendOpen = signal<boolean>(false);
  private readonly rowOrder = signal<string[]>([]);
  /** The lens the rows were last sorted in. */
  private readonly sortedLens = signal<EGoalsLens>(EGoalsLens.deliverables);
  private readonly pendingDeepLink = signal<IGoalsDeepLink | null>(null);

  private readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  /** The global date filter defaults to the campaign's days; anything shorter hides part of the plan. */
  public readonly isDateRangeNarrowed = computed(() => {
    const campaign = this.campaign();
    const { endDate, startDate } = this.campaignAnalyticsFilter();

    if (!campaign) {
      return false;
    }

    return (
      (!!startDate && isAfter(startOfDay(startDate), startOfDay(new Date(campaign.start)))) ||
      (!!endDate && isBefore(startOfDay(endDate), startOfDay(new Date(campaign.end))))
    );
  });

  public readonly matrix = computed(() =>
    buildGoalsMatrix({
      analyticsUtilities: this.analyticsUtilities,
      ctaTemplates: this.campaign()?.ctaTemplates ?? [],
      deliverableUtilities: this.deliverableUtilities,
      exportConfig: this.exportMode() ? this.campaignExportConfig() : null,
      filter: this.campaignAnalyticsFilter(),
      isDateRangeNarrowed: this.isDateRangeNarrowed(),
      nodes: this.analyticsNodes() ?? [],
      shell: this.campaignAnalytics(),
    }),
  );

  public readonly kpiGoalCount = computed(() =>
    this.matrix().columns.reduce((sum, column) => sum + column.templateKpis.length, 0),
  );

  public readonly showLensSwitch = computed(
    () =>
      !this.exportMode() || (this.matrix().hasKpiGoals && this.campaignExportConfig().allowedKpiStatuses?.length !== 0),
  );

  public readonly lensOptions = computed<ISegmentedControlOption<EGoalsLens>[]>(() => [
    { label: 'pages.campaignDetails.goals.lens.deliverables', value: EGoalsLens.deliverables },
    {
      count: this.kpiGoalCount(),
      disabled: !this.matrix().hasKpiGoals,
      label: 'pages.campaignDetails.goals.lens.kpis',
      tooltip: this.matrix().hasKpiGoals ? null : 'pages.campaignDetails.goals.lens.kpisDisabled',
      value: EGoalsLens.kpis,
    },
  ]);

  /** Bar layout per group column, from its longest bar: cell, sub-row and footer bars share one grid. */
  public readonly columnLayouts = computed(
    () =>
      new Map(
        this.matrix().columns.map((column) => {
          const capacity = getGoalsColumnCapacity(this.matrix().rows, column.ctaId);

          return [column.ctaId, { capacity, ...getSlotBarLayout(capacity) }];
        }),
      ),
  );

  public readonly gridColumns = computed<IGoalsGridColumn[]>(() => [
    { group: null, id: 'creator', kind: 'creator', label: '', sticky: true, width: CREATOR_COLUMN_WIDTH },
    { group: null, id: 'plan', kind: 'plan', label: '', sticky: true, width: PLAN_COLUMN_WIDTH },
    ...this.matrix().columns.map(
      (group): IGoalsGridColumn => ({
        collapsible: true,
        group,
        id: group.ctaId,
        kind: 'group',
        label: group.name,
        width: GROUP_COLUMN_WIDTH,
      }),
    ),
    // Without them the skeleton would have no group columns and a one-line header, both jumping once the groups load.
    ...(this.loading() && !this.campaign()
      ? Array.from(
          { length: PLACEHOLDER_COLUMN_COUNT },
          (_, index): IGoalsGridColumn => ({
            group: null,
            id: `placeholder-${index}`,
            kind: 'placeholder',
            label: '',
            width: GROUP_COLUMN_WIDTH,
          }),
        )
      : []),
  ]);

  public readonly gridRows = computed<IGoalsGridRow[]>(() => {
    const rowsById = new Map(this.matrix().rows.map((row) => [row.creatorId, row]));
    const ordered = this.rowOrder()
      .map((id) => rowsById.get(id))
      .filter((row) => !!row);
    const orderedIds = new Set(ordered.map((row) => row.creatorId));

    return [...ordered, ...this.matrix().rows.filter((row) => !orderedIds.has(row.creatorId))].map((row) => ({
      ...row,
      id: row.creatorId,
    }));
  });

  public readonly hiddenColumnCount = computed(() => {
    const hidden = new Set(this.collapsedColumnIds());

    return this.matrix().columns.filter((column) => hidden.has(column.ctaId)).length;
  });

  /** Lens switch and legend only make sense with a table to read. */
  public readonly hasTable = computed(() => !this.loadError() && (this.loading() || !!this.gridRows().length));

  /** Drives the sort arrows; null once a lens switch left a progress sort that no longer matches the rows. */
  public readonly shownSort = computed(() => getGoalsShownSort(this.sort(), this.sortedLens(), this.lens()));

  /** Marks the sorted column header (`aria-sort`); the sort buttons keep their visible text as their name. */
  public readonly gridSort = computed<IMatrixGridSort | null>(() => {
    const sort = this.shownSort();

    return sort && { columnId: sort.key === EGoalsSortKey.creator ? 'creator' : 'plan', direction: sort.direction };
  });

  public readonly allRowsExpanded = computed(() =>
    areAllGoalsRowsExpanded(
      this.gridRows().map((row) => row.id),
      this.expandedRowIds(),
    ),
  );

  public readonly planTotal = computed(() => getGoalsPlanTotal(this.matrix().rows, this.lens()));
  /** Deliverables-lens footer line per group: its label and its Contents link. */
  public readonly footerSummaries = computed(() => {
    const nodes = this.analyticsNodes();
    const exportConfig = this.campaignExportConfig();

    return new Map(
      this.matrix().columns.map((column) => [
        column.ctaId,
        getGoalsFooterSummary(column.ctaId, column.footer[EGoalsLens.deliverables], nodes, exportConfig),
      ]),
    );
  });

  /** A group with nothing on the Deliverables tab keeps its header, tag and footer as plain text. */
  /** The Deliverables tab's own rows decide where a link has something to show (same nodes, same builder). */
  private readonly deliverablesLinkKeys = computed(() =>
    buildGoalsDeliverablesLinkKeys(this.deliverablesService.buildRows(this.analyticsNodes(), this.translate)),
  );

  public readonly columnHeaders = computed(() => {
    this.languageChange();
    const lens = this.lens();
    const headers = new Map<string, IGoalsColumnHeaderView>();

    for (const column of this.matrix().columns) {
      const kpiHeader = getGoalsKpiHeaderText(
        column.templateKpis.map((kpi) => this.getKpiTargetText(kpi)),
        (key, params) => this.translate.instant(key, params),
      );
      const subText = lens === EGoalsLens.kpis ? kpiHeader.text : this.getDeliverablesText(column.deliverableCount);

      headers.set(column.ctaId, {
        frequency:
          column.frequency === 'required'
            ? {
                key: column.hasEveryStream ? 'requiredPlusEveryStream' : 'required',
                params: { count: column.requiredCount },
              }
            : { key: column.frequency },
        subText,
        subTooltip: lens === EGoalsLens.kpis ? kpiHeader.tooltip : subText,
      });
    }

    return headers;
  });

  public readonly cellViews = computed(() => {
    this.languageChange();
    const lens = this.lens();
    const canOpenContents = this.canOpenContents();
    const exportConfig = this.campaignExportConfig();
    const views = new Map<string, IGoalsCellView>();

    for (const row of this.matrix().rows) {
      for (const cell of Object.values(row.cells)) {
        const notes = getGoalsCellNotes(cell, lens);

        views.set(this.cellKey(cell), {
          bar: this.buildSlotBar(cell, lens),
          links: canOpenContents ? getGoalsCellLinks(cell, notes, exportConfig, lens) : [],
          subRows: lens === EGoalsLens.kpis ? this.buildKpiSubRows(cell) : this.buildDeliverableSubRows(cell),
          summary: notes
            .map((note) => this.translate.instant(`pages.campaignDetails.goals.notes.${note.key}`, note.params))
            .join(' · '),
        });
      }
    }

    return views;
  });

  private readonly hasSlotsWithoutKpiGoals = computed(() => hasGoalsKpiBarSlotsWithoutGoals(this.matrix().rows));

  public readonly legendItems = computed<IStatusLegendItem[]>(() => {
    const items: IStatusLegendItem[] =
      this.lens() === EGoalsLens.kpis
        ? [
            { label: 'pages.campaignDetails.goals.legend.allReached', tone: 'success' },
            { label: 'pages.campaignDetails.goals.legend.someBelow', tone: 'warning' },
            { label: 'pages.campaignDetails.goals.legend.allBelow', tone: 'error' },
            { label: 'pages.campaignDetails.goals.legend.noData', tone: 'hatched' },
            ...(this.hasSlotsWithoutKpiGoals()
              ? [{ label: 'pages.campaignDetails.goals.legend.noKpiGoals', tone: 'neutral' as const }]
              : []),
            { label: 'pages.campaignDetails.goals.legend.notDelivered', tone: 'empty' },
          ]
        : [
            { label: 'shared.statuses.fulfilled', tone: 'success' },
            { label: 'shared.statuses.partiallyFulfilled', tone: 'warning' },
            { label: 'shared.statuses.notFulfilled', tone: 'error' },
            { label: 'shared.statuses.processing', tone: 'processing' },
            { label: 'pages.campaignDetails.goals.legend.notDelivered', tone: 'empty' },
          ];

    if (this.exportMode()) {
      items.push({ label: 'pages.campaignDetails.goals.legend.hidden', tone: 'neutral' });
    }

    return items;
  });

  constructor() {
    // Re-sort only on new data or a sort header click, never on a lens switch: rows must stay put while comparing lenses.
    effect(() => {
      this.matrix();

      untracked(() => this.applySort(this.sort()));
    });

    effect(() => {
      const campaignId = this.campaign()?.id;

      if (campaignId) {
        untracked(() =>
          this.collapsedColumnIds.set(this.collapseState.load(campaignId, this.userId(), this.exportMode())),
        );
      }
    });

    effect(() => {
      const deepLink = this.deepLink();

      if (deepLink) {
        untracked(() => this.pendingDeepLink.set(deepLink));
      }
    });

    // A drill-down can arrive while the tab's data is still loading: wait until there are rows to show.
    effect(() => {
      const deepLink = this.pendingDeepLink();

      if (deepLink && !this.loading() && this.matrix().rows.length) {
        untracked(() => {
          this.pendingDeepLink.set(null);
          this.applyDeepLink(deepLink);
        });
      }
    });

    effect(() => {
      if (!this.loading() && !this.matrix().hasKpiGoals && this.lens() === EGoalsLens.kpis) {
        untracked(() => this.lens.set(EGoalsLens.deliverables));
      }
    });
  }

  public cellKey(cell: Pick<IGoalsCell, 'creatorId' | 'ctaId'>): string {
    return `${cell.creatorId}::${cell.ctaId}`;
  }

  public onSort(key: EGoalsSortKey): void {
    const current = this.shownSort();
    const sort: IGoalsSort =
      current?.key === key
        ? { direction: current.direction === 'asc' ? 'desc' : 'asc', key }
        : { direction: 'asc', key };

    this.sort.set(sort);
    this.applySort(sort);
  }

  /** Through the grid, which also moves focus off the disappearing "Show all" button. */
  public showAllColumns(): void {
    this.grid()?.showAllColumns();
  }

  public onCollapsedColumnIdsChange(ids: string[]): void {
    this.collapsedColumnIds.set(ids);

    const campaignId = this.campaign()?.id;

    if (campaignId) {
      this.collapseState.save(campaignId, this.userId(), this.exportMode(), ids);
    }
  }

  /**
   * A group's contents on the Contents tab, for one creator or (no row) all of them; without types, every type but
   * "unrelated" (the tab's default).
   */
  public openContents(
    row: IGoalsGridRow | null,
    ctaId: string,
    { contentStatuses, matchingTypes }: Partial<IGoalsContentsLink> = {},
  ): void {
    this.contentsRequested.emit({
      contentStatuses: contentStatuses ? new Set(contentStatuses) : undefined,
      creator: row ? this.toCreatorInfo(row) : undefined,
      group: getGoalsLinkGroup(this.campaignAnalytics(), this.matrix().columns, ctaId),
      matchingTypes: new Set(matchingTypes ?? GOALS_RELATED_MATCHING_TYPES),
      tab: ECampaignDetailsTab.contents,
    });
  }

  /** Without rows for these filters the link would open an empty or unfiltered tab, so the text stays plain. */
  public canOpenDeliverablesLink({ ctaId, deliverableType, groupWide, row }: IGoalsDeliverablesLink = {}): boolean {
    return (
      this.canOpenDeliverables() &&
      hasGoalsDeliverablesLinkRows(this.deliverablesLinkKeys(), groupWide ? 'group' : 'counted', {
        creatorId: row?.creatorId,
        ctaId,
        deliverableType,
      })
    );
  }

  public openDeliverables({ ctaId, deliverableType, groupWide, row }: IGoalsDeliverablesLink = {}): void {
    this.deliverablesRequested.emit({
      creator: row ? this.toCreatorInfo(row) : undefined,
      ctaTemplateIds: ctaId ? new Set([ctaId]) : undefined,
      deliverableTypes: deliverableType ? new Set([deliverableType]) : undefined,
      matchingTypes: new Set(groupWide ? GOALS_RELATED_MATCHING_TYPES : GOALS_COUNTED_MATCHING_TYPES),
      tab: ECampaignDetailsTab.deliverables,
    });
  }

  public onSlotClick(cell: IGoalsCell, index: number): void {
    const { analyticsNode } = cell.slots[index];

    if (analyticsNode) {
      this.analyticsRequested.emit({ analyticsNode, ctaId: cell.ctaId });
    }
  }

  public onSubRowSlotClick(subRow: IGoalsSubRowView, index: number): void {
    const request = subRow.requests[index];

    if (request) {
      this.analyticsRequested.emit(request);
    }
  }

  public countWidth(cell: IGoalsCell): number {
    return getGoalsCountWidth(cell);
  }

  public getCellAriaLabel(row: IGoalsGridRow, cell: IGoalsCell, summary: string): string {
    return this.translate.instant('pages.campaignDetails.goals.cell.openContents', {
      creator: row.creatorName,
      done: cell.lenses[this.lens()].done,
      group: cell.ctaName,
      required: cell.required,
      summary: summary ? `, ${summary}` : '',
    });
  }

  public exportCsv(): void {
    const csv = buildGoalsCsv(this.matrix(), this.gridRows(), {
      translate: (key, params) => this.translate.instant(key, params),
    });

    downloadCsvFile(csv, getGoalsCsvFileName(this.campaign()?.name ?? '', new Date()));
  }

  private userId(): string | null {
    return AuthService.currentDbUser?.id ?? null;
  }

  /** Orders the rows by the current lens and remembers that lens for the sort arrows. */
  private applySort(sort: IGoalsSort): void {
    this.sortedLens.set(this.lens());
    this.rowOrder.set(sortGoalsRows(this.matrix().rows, sort, this.lens()).map((row) => row.creatorId));
  }

  private applyDeepLink(deepLink: IGoalsDeepLink): void {
    if (this.matrix().hasKpiGoals) {
      this.lens.set(EGoalsLens.kpis);
      // Landing in the KPIs lens, not a switch: the rows take that lens's order.
      this.applySort(this.sort());
    }

    if (!deepLink.creatorId) {
      return;
    }

    const { creatorId } = deepLink;

    this.expandedRowIds.update((ids) => (ids.includes(creatorId) ? ids : [...ids, creatorId]));
    setTimeout(() =>
      document
        .querySelector(`[data-testid="matrix-grid-row"][data-row-id="${CSS.escape(creatorId)}"] [role="cell"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    );
  }

  private buildSlotBar(cell: IGoalsCell, lens: EGoalsLens): ISlotBarSlot[] {
    const cellLens = cell.lenses[lens];
    const canOpen = this.campaignExportConfig().allowDeliverableGroupClick;

    return cell.slots.map((slot, index) => ({
      clickable: canOpen && slot.kind === 'counted' && !!slot.analyticsNode,
      tone: cellLens.tones[index],
      tooltip: this.getSlotTooltip(slot, index, getGoalsSlotLabelKey(slot, lens, cellLens.slotHidden[index])),
    }));
  }

  private getSlotTooltip(slot: IGoalsSlot, index: number, labelKey: string | null): string {
    const parts = [`#${index + 1}`];

    if (labelKey) {
      parts.push(this.translate.instant(labelKey));
    }

    if (slot.kind === 'counted' && slot.executionTime) {
      parts.push(this.dateUtils.formatDate(this.dateUtils.parseDateTimeFromApi(slot.executionTime), DATE_FORMAT));
    }

    return parts.join(' · ');
  }

  private buildDeliverableSubRows(cell: IGoalsCell): IGoalsSubRowView[] {
    const canOpen = this.campaignExportConfig().allowDeliverablesRowClick;

    return cell.deliverableRows.map((row) => {
      const requests = cell.slots.map((slot): IGoalsAnalyticsRequest | null =>
        canOpen &&
        slot.analyticsNode &&
        slot.deliverables.some((deliverable) => deliverable.deliverableId === row.deliverableId)
          ? {
              analyticsNode: slot.analyticsNode,
              ctaId: cell.ctaId,
              deliverableId: row.deliverableId,
              deliverableType: row.type,
            }
          : null,
      );

      return {
        bar: this.buildSubRowBar(cell, row, requests),
        deliverableType: row.type,
        id: row.deliverableId,
        label: this.translate.instant('pages.campaignDetails.goals.subRow.fulfilled', {
          done: row.fulfilled,
          total: row.total,
        }),
        name: row.name,
        requests,
      };
    });
  }

  private buildKpiSubRows(cell: IGoalsCell): IGoalsSubRowView[] {
    const canOpen = this.campaignExportConfig().allowKpiGoalsRowClick;

    return cell.kpiRows.map((row) => {
      const requests = cell.slots.map((slot): IGoalsAnalyticsRequest | null => {
        const slotKpi = slot.kpis.find((kpi) => kpi.key === row.key);

        return canOpen && slot.analyticsNode && slotKpi
          ? {
              analyticsNode: slot.analyticsNode,
              ctaId: cell.ctaId,
              expectedKpiId: slotKpi.expectedKpiId,
              kpiType: row.kpiType,
            }
          : null;
      });
      const reached = this.translate.instant('pages.campaignDetails.goals.subRow.reached', {
        done: row.reached,
        total: row.total,
      });
      const average =
        row.average === null
          ? null
          : this.translate.instant('pages.campaignDetails.goals.subRow.average', {
              value: this.formatNumber(row.average),
            });

      return {
        bar: this.buildSubRowBar(cell, row, requests),
        id: row.key,
        label: average ? `${reached} · ${average}` : reached,
        name: `${this.translate.instant(`shared.expectedKpis.${row.kpiType}`)} ≥ ${this.formatNumber(row.target)}`,
        requests,
      };
    });
  }

  /** Sub-row blocks follow the group bar's slot order; each opens its own content. */
  private buildSubRowBar(
    cell: IGoalsCell,
    row: IGoalsDeliverableRow | IGoalsKpiRow,
    requests: (IGoalsAnalyticsRequest | null)[],
  ): ISlotBarSlot[] {
    return row.tones.map((tone, index) => ({
      clickable: !!requests[index],
      tone,
      tooltip: this.getSlotTooltip(cell.slots[index], index, row.labels[index]),
    }));
  }

  private getDeliverablesText(count: number): string {
    return count === 1
      ? this.translate.instant('pages.campaignDetails.goals.header.deliverable')
      : this.translate.instant('pages.campaignDetails.goals.header.deliverables', { count });
  }

  private toCreatorInfo(row: IGoalsGridRow): IDeepLinkCreatorInfo {
    return { icon: row.creatorIcon, id: row.creatorId, name: row.creatorName };
  }

  private getKpiTargetText(kpi: { kpiType: string; target: number }): string {
    return `${this.translate.instant(`shared.expectedKpis.${kpi.kpiType}`)} ≥ ${this.formatNumber(kpi.target)}`;
  }

  private formatNumber(value: number): string {
    return this.numberUtilities.getNumberWithSuffix(value, 0, 1);
  }
}
